import {
  CmcComparison,
  SearchFilterState,
  StatComparison,
  normalizeColors,
} from './searchState';

export interface ParsedSyntax {
  /** Filters recognized in the input. */
  patch: Partial<SearchFilterState>;
  /** Everything that was not a recognized operator, used as the name filter. */
  leftover: string;
}

/**
 * Operators map onto filters that already exist. Anything else is left alone
 * rather than rejected — an unparseable token becomes part of the name search,
 * so the user always gets results instead of an error.
 */
const FIELD_ALIASES: Record<string, keyof SearchFilterState> = {
  t: 'typeLine',
  type: 'typeLine',
  o: 'oracleText',
  oracle: 'oracleText',
  text: 'oracleText',
  s: 'setName',
  set: 'setName',
  e: 'setName',
  a: 'artist',
  artist: 'artist',
  ft: 'flavorText',
  flavor: 'flavorText',
  f: 'format',
  format: 'format',
  m: 'manaCost',
  mana: 'manaCost',
  name: 'name',
};

const NUMERIC_FIELDS: Record<string, 'cmc' | 'power' | 'toughness' | 'points'> = {
  mv: 'cmc',
  cmc: 'cmc',
  pow: 'power',
  power: 'power',
  tou: 'toughness',
  toughness: 'toughness',
  pts: 'points',
  points: 'points',
};

const OPERATOR_TO_STAT: Record<string, StatComparison> = {
  '=': 'eq',
  ':': 'eq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
};

const OPERATOR_TO_CMC: Record<string, CmcComparison> = {
  '=': 'eq',
  ':': 'eq',
  '>': 'gt',
  '<': 'lt',
  // The backend has no >=/<= for mana value; fall back to the strict form.
  '>=': 'gt',
  '<=': 'lt',
};

/** field:value, field=value, field>=value, and a quoted-value form. */
const TOKEN = /(\w+)(>=|<=|[:=><])("([^"]*)"|'([^']*)'|\S+)/g;

const COLOR_LETTERS = /^[wubrgc]+$/i;

/**
 * Parse a compact query such as `t:instant mv<=2 c:ur bolt`.
 *
 * Only operators that map onto an existing filter are recognized. Unrecognized
 * text falls through to `leftover`, which the caller applies as the name filter.
 */
export const parseSyntax = (input: string): ParsedSyntax => {
  const patch: Partial<SearchFilterState> = {};
  // Consumed spans are blanked by index rather than by string replace: a
  // replace removes the FIRST textual occurrence, which for input like
  // "zzz:c:ur c:ur" is not the span that actually matched.
  const chars = input.split('');

  const matches = Array.from(input.matchAll(TOKEN));

  for (const match of matches) {
    const [full, rawField, operator, rawValue, doubleQuoted, singleQuoted] = match;
    const field = rawField.toLowerCase();
    const value = (doubleQuoted ?? singleQuoted ?? rawValue).trim();
    if (!value) continue;

    let consumed = true;

    if (field === 'c' || field === 'color' || field === 'colors' || field === 'id') {
      if (COLOR_LETTERS.test(value)) {
        patch.colors = normalizeColors(value.toUpperCase().split(''));
      } else {
        consumed = false;
      }
    } else if (NUMERIC_FIELDS[field]) {
      const target = NUMERIC_FIELDS[field];
      const numeric = value.replace(/[^\d-]/g, '');
      if (!numeric) {
        consumed = false;
      } else if (target === 'cmc') {
        patch.cmc = numeric.replace(/-/g, '');
        patch.cmcComparison = OPERATOR_TO_CMC[operator] ?? 'eq';
      } else if (target === 'power') {
        patch.power = numeric;
        patch.powerComparison = OPERATOR_TO_STAT[operator] ?? 'eq';
      } else if (target === 'toughness') {
        patch.toughness = numeric;
        patch.toughnessComparison = OPERATOR_TO_STAT[operator] ?? 'eq';
      } else {
        // Points take a bound rather than a comparison, so an equals reads as
        // "at least this much" instead of silently doing nothing.
        const digits = numeric.replace(/-/g, '');
        if (operator === '<' || operator === '<=') patch.pointsMax = digits;
        else patch.pointsMin = digits;
      }
    } else if (FIELD_ALIASES[field]) {
      const key = FIELD_ALIASES[field];
      if (key === 'format') {
        patch.format = value.toLowerCase();
      } else {
        (patch as Record<string, string>)[key] = value;
      }
    } else {
      consumed = false;
    }

    if (consumed && match.index !== undefined) {
      for (let i = match.index; i < match.index + full.length; i++) chars[i] = ' ';
    }
  }

  return { patch, leftover: chars.join('').replace(/\s+/g, ' ').trim() };
};

/**
 * Apply a parsed query on top of a state. Leftover text becomes the name filter,
 * which is what makes a plain "black lotus" behave exactly as before.
 */
export const applySyntax = (
  state: SearchFilterState,
  input: string,
): SearchFilterState => {
  const { patch, leftover } = parseSyntax(input);
  return { ...state, ...patch, name: leftover };
};

export default parseSyntax;
